import { platform } from '../device/platform'
import { EventProps, UserProfile, IEventTracker, ITrackingProvider, TrackingInitOptions } from '../../interfaces/event-tracker'
import { GTMProvider } from './providers/GTMProvider'
import { AmplitudeWebProvider } from './providers/AmplitudeWebProvider'
import { FirebaseNativeProvider } from './providers/FirebaseNativeProvider'
import { SentryProvider } from './providers/SentryProvider'

const isNative = platform !== 'web'

export class EventTracker implements IEventTracker {
  private static instance: EventTracker | null = null
  private providers: ITrackingProvider[] = []
  private initialized = false

  private initOptions: TrackingInitOptions = {}

  static getInstance (): EventTracker {
    if (EventTracker.instance == null) {
      EventTracker.instance = new EventTracker()
    }
    return EventTracker.instance
  }

  init (gtmId?: string): void {
    if (this.initialized) return
    this.initialized = true

    this.initOptions = isNative ? { appVersion: process.env.APP_VERSION } : {}

    const amplitudeKey = process.env.AMPLITUDE_API_KEY ?? ''

    if (isNative) {
      // Mobile: Firebase Analytics nativo (hilo nativo, no bloquea JS)
      // + Amplitude browser SDK (puro JS, sin native lib → sin problemas 16KB page alignment)
      this.providers.push(new FirebaseNativeProvider())
      if (amplitudeKey !== '') {
        this.providers.push(new AmplitudeWebProvider(amplitudeKey))
      }
    } else {
      // Web: GTM + Amplitude browser SDK
      if (gtmId != null && gtmId !== '') {
        this.providers.push(new GTMProvider(gtmId))
      }
      if (amplitudeKey !== '') {
        this.providers.push(new AmplitudeWebProvider(amplitudeKey))
      }
    }

    // Sentry en ambas plataformas (ya inicializado en preInit)
    this.providers.push(new SentryProvider())

    // Inicializar providers de forma no bloqueante
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => { this.initProviders() }, { timeout: 10000 })
    } else {
      setTimeout(() => { this.initProviders() }, 5000)
    }
  }

  trackEvent (props: EventProps): void {
    for (const provider of this.providers) {
      try { provider.trackEvent(props) } catch {}
    }
  }

  setUser (user: UserProfile): void {
    for (const provider of this.providers) {
      try { provider.setUser(user) } catch {}
    }
  }

  resetUser (): void {
    for (const provider of this.providers) {
      try { provider.setUser({ uid: '', aid: '', email: '' } as UserProfile) } catch {}
    }
  }

  private initProviders (): void {
    for (const provider of this.providers) {
      try {
        const result = provider.init(this.initOptions)
        if (result instanceof Promise) result.catch(() => {})
      } catch {}
    }
  }
}
